/**
 * Run summary for the clipboard - ticket, PR links and total cost of a run
 *
 * Built twice: HTML so a paste into Slack, Jira, Docs or email keeps the
 * ticket identifier as a link, and plain text with the URLs spelled out for
 * anywhere that drops formatting.
 */

import { formatCost } from './format-utils';

export interface RunSummaryInput {
  ticketKey: string;
  ticketUrl: string | null;
  prUrls: string[];
  /** Omitted when unknown - only admins can see usage */
  totalCostCents?: number;
}

export interface RunSummary {
  html: string;
  text: string;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function htmlLink(url: string, label: string): string {
  return `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`;
}

export function buildRunSummary({
  ticketKey,
  ticketUrl,
  prUrls,
  totalCostCents,
}: RunSummaryInput): RunSummary {
  const text = [ticketUrl ? `${ticketKey}: ${ticketUrl}` : ticketKey];
  const html = [ticketUrl ? htmlLink(ticketUrl, ticketKey) : escapeHtml(ticketKey)];

  for (const prUrl of prUrls) {
    text.push(`PR: ${prUrl}`);
    html.push(`PR: ${htmlLink(prUrl, prUrl)}`);
  }

  if (totalCostCents !== undefined) {
    const cost = `Cost: ${formatCost(totalCostCents)}`;
    text.push(cost);
    html.push(cost);
  }

  return { text: text.join('\n'), html: html.join('<br>') };
}
